import React, { useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Carousel from 'react-native-reanimated-carousel';

import { InnerBorder } from '@/components/InnerBorder';
import { Text } from '@/components/TextComponent';
import { Box, Image } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Tag } from '@/feature/lobby/components/EventHeader';
import { common, designSystem } from '@/styles/styles';

import { CasinoGame } from '../../hooks/use-casino-games';
import { useCasinoLobbyContentWidth } from '../../hooks/use-casino-lobby-content-width';
import { useStartCasinoGameUrl } from '../../hooks/use-formatted-casino-game-url';
import { CASINO_TAG_COLORS, CasinoLayoutProps } from '../../utils/utils';
import { LayoutHeader } from './LayoutHeader';

const CarouselGameCard = ({ game }: { game: CasinoGame }) => {
    const { handleStartGame } = useStartCasinoGameUrl(game);

    return (
        <Box
            height={184}
            style={styles.cardContainer}
            borderRadius="r16"
            overflow="hidden"
            backgroundColor={ds.colorToken.background.neutral.subtler}
        >
            <InnerBorder
                borderRadius="r16"
                borderColor={ds.colorValue.border.strong}
                boxProps={{ style: common.zIndex1 }}
            />

            {game.backgroundImageUrl ? (
                <Image
                    source={{ uri: `https:${game.backgroundImageUrl}` }}
                    style={styles.backgroundImage}
                    resizeMode="cover"
                />
            ) : (
                <Box style={styles.placeholderBackground} backgroundColor={ds.colorToken.background.neutral.subtler} />
            )}

            <Box style={styles.gradientOverlay} />

            {game.exclusive?.effective ? (
                <Box position="absolute" top={16} left={16} style={common.zIndex2}>
                    <Tag label="Featured" color={CASINO_TAG_COLORS.generic} />
                </Box>
            ) : null}

            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                flexDirection="row"
                alignItems="flex-end"
                justifyContent="space-between"
                padding="s16"
                style={common.zIndex2}
            >
                <Box flexDirection="row" alignItems="flex-end" flex={1} mr="s12">
                    {game.thumbnail ? (
                        <Box
                            width={48}
                            height={48}
                            borderRadius="r12"
                            overflow="hidden"
                            backgroundColor={ds.colorToken.background.neutral.subtler}
                            style={styles.thumbnailShadow}
                        >
                            <InnerBorder
                                borderRadius="r12"
                                borderColor={ds.colorValue.border.strong}
                                boxProps={{ style: common.zIndex1 }}
                            />
                            <Image
                                source={{ uri: `https:${game.thumbnail}` }}
                                style={styles.thumbnail}
                                resizeMode="cover"
                            />
                        </Box>
                    ) : null}

                    <Box flex={1} ml={game.thumbnail ? 's12' : undefined}>
                        <Text color={ds.colorToken.text.primary} variant="titleMedium" numberOfLines={2}>
                            {game.name}
                        </Text>
                    </Box>
                </Box>

                <TouchableOpacity onPress={handleStartGame} activeOpacity={0.7}>
                    <Box
                        backgroundColor={ds.colorToken.background.success.strong}
                        paddingHorizontal="s20"
                        paddingVertical="s12"
                        borderRadius="r12"
                        style={styles.playButton}
                    >
                        <Text color={ds.colorToken.text.primary} variant="bodyMedium" fontWeight="bold">
                            Play Now
                        </Text>
                    </Box>
                </TouchableOpacity>
            </Box>
        </Box>
    );
};

export const FeaturedCarouselLayout = ({ data, layoutHeaderProps }: CasinoLayoutProps) => {
    const width = useCasinoLobbyContentWidth();
    const scrollProgress = useSharedValue<number>(0);

    const renderItem = useCallback(({ item }: { item: CasinoGame }) => {
        return <CarouselGameCard game={item} />;
    }, []);

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Box>
            {layoutHeaderProps ? <LayoutHeader {...layoutHeaderProps} /> : null}

            <Box pb="s24">
                <Carousel
                    data={data}
                    renderItem={renderItem}
                    width={width}
                    height={184}
                    loop={true}
                    autoPlay={true}
                    autoPlayInterval={5000}
                    onProgressChange={scrollProgress}
                    pagingEnabled={true}
                />
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    cardContainer: {
        borderCurve: 'continuous',
        position: 'relative',
    },
    backgroundImage: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
    },
    placeholderBackground: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
    },
    gradientOverlay: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    thumbnail: {
        width: '100%',
        height: '100%',
    },
    thumbnailShadow: {
        shadowColor: designSystem.colors.neutralBlack,
        shadowOpacity: 0.5,
        shadowRadius: 16,
        elevation: 6,
        shadowOffset: { width: 0, height: 4 },
    },
    playButton: {
        shadowColor: ds.colorValue.background.success.strong,
        shadowOpacity: 0.4,
        shadowRadius: 12,
        elevation: 4,
        shadowOffset: { width: 0, height: 4 },
    },
});
